import { PollIcon, cn } from '@playerbase/ui';

import { FeedbackRow } from './feedback-row.js';
import type { InboxGroup, InboxMessage } from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Polls feed entry.
//
// Wraps FeedbackRow with a rich poll panel above: the question, every
// answer with vote count and a thin progress bar, the leading-vote
// answer marked with a filled accent dot, plus a footer that shows
// the total vote count, an Active / Closed status pill, and either
// "closes in 2d 4h" or "closed {relative time}". The FeedbackRow
// underneath keeps the avatar / author / channel / reactions / reply
// context, so click-to-source navigation and highlight pulses work
// the same as in the message feed.
//
// hidePollPill={true} on FeedbackRow suppresses the inline emoji pill
// it would otherwise render for poll-bearing messages - the rich
// panel here is its replacement, and double-rendering the question
// would be visual noise.
// ---------------------------------------------------------------------------

interface PollGroupCardProps {
  group: InboxGroup;
  byExternalId: Map<string, InboxMessage>;
  highlighted?: boolean;
  innerRef?: ((el: HTMLDivElement | null) => void) | undefined;
}

const ONE_MINUTE_MS = 60_000;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

function formatRelativePast(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  if (diff < ONE_MINUTE_MS) return 'just now';
  if (diff < ONE_HOUR_MS) return `${Math.floor(diff / ONE_MINUTE_MS)}m ago`;
  if (diff < ONE_DAY_MS) return `${Math.floor(diff / ONE_HOUR_MS)}h ago`;
  const days = Math.floor(diff / ONE_DAY_MS);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

// "closes in 2d 4h" / "closes in 45m". Two largest non-zero units to
// keep the footer compact without rounding too aggressively.
function formatTimeUntil(timestamp: string): string {
  const diff = new Date(timestamp).getTime() - Date.now();
  if (diff <= 0) return 'closing now';
  const days = Math.floor(diff / ONE_DAY_MS);
  const hours = Math.floor((diff % ONE_DAY_MS) / ONE_HOUR_MS);
  const minutes = Math.floor((diff % ONE_HOUR_MS) / ONE_MINUTE_MS);
  if (days >= 1) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours >= 1) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${Math.max(1, minutes)}m`;
}

function isClosed(
  finalised: boolean,
  expiresAt: string | null,
  now: number,
): boolean {
  if (finalised) return true;
  if (expiresAt === null) return false;
  return new Date(expiresAt).getTime() <= now;
}

interface PollAnswerRowProps {
  text: string | null;
  emoji: { id: string | null; name: string | null; animated: boolean | null } | null;
  voteCount: number;
  totalVotes: number;
  isLeader: boolean;
  closed: boolean;
}

function customEmojiUrl(id: string, animated: boolean | null): string {
  const ext = animated === true ? 'gif' : 'png';
  return `https://cdn.discordapp.com/emojis/${id}.${ext}`;
}

function PollAnswerRow({
  text,
  emoji,
  voteCount,
  totalVotes,
  isLeader,
  closed,
}: PollAnswerRowProps): JSX.Element {
  const pct = totalVotes === 0 ? 0 : Math.round((voteCount / totalVotes) * 100);
  return (
    <li className="flex items-center gap-2 text-sm">
      <span
        aria-hidden
        className={cn(
          'block h-2 w-2 shrink-0 rounded-full',
          // Filled accent on the leading answer to make the current
          // winner readable at a glance. When the poll is closed the
          // leader becomes the result; when active it's just a
          // running indication.
          isLeader && voteCount > 0
            ? closed
              ? 'bg-teal'
              : 'bg-accent'
            : 'border border-border bg-transparent',
        )}
      />
      {emoji !== null && (
        emoji.id !== null ? (
          <img
            src={customEmojiUrl(emoji.id, emoji.animated)}
            alt={emoji.name ?? ''}
            className="h-4 w-4"
            loading="lazy"
          />
        ) : (
          <span aria-label={emoji.name ?? 'reaction'} className="text-sm leading-none">
            {emoji.name ?? ''}
          </span>
        )
      )}
      <span className="min-w-0 flex-1 truncate text-text">{text ?? ''}</span>
      <span className="tabular-nums text-text-muted text-xs">
        {voteCount} {voteCount === 1 ? 'vote' : 'votes'}
      </span>
      <span className="tabular-nums text-text-muted text-xs w-9 text-right">
        {pct}%
      </span>
      <span
        aria-hidden
        className="block h-1 w-20 shrink-0 overflow-hidden rounded-full bg-surface-3"
      >
        <span
          className={cn(
            'block h-full transition-[width] duration-300 ease-out',
            closed ? 'bg-teal' : 'bg-accent',
          )}
          style={{ width: `${pct}%` }}
        />
      </span>
    </li>
  );
}

export function PollGroupCard({
  group,
  byExternalId,
  highlighted = false,
  innerRef,
}: PollGroupCardProps): JSX.Element {
  const head = group.head;
  // Polls only land on a single message; head.messages has it as the
  // first (and usually only) entry. Defensive find rather than [0] in
  // case the grouping pipeline ever bundles a poll with a sibling.
  const pollMessage = head.messages.find((m) => m.poll !== null);
  const poll = pollMessage?.poll ?? null;
  const status = head.pollStatus;
  if (poll === null || status === null) {
    // Should never hit - the polls feed only renders groups whose
    // head carries a poll - but rather than crash, fall through to
    // the bare row so the entry is at least recognisable.
    return (
      <div ref={innerRef}>
        <FeedbackRow row={head} byExternalId={byExternalId} hidePollPill />
      </div>
    );
  }

  const now = Date.now();
  const closed = isClosed(status.finalised, status.expiresAt, now);
  const totalVotes = status.totalVotes;
  // Leading-vote tiebreak: if multiple answers share the max count,
  // mark the first one in answer order as leader. Voters reading
  // a tied poll see "no clear leader" via the equal bars regardless.
  const maxVotes = poll.answers.reduce((m, a) => Math.max(m, a.voteCount), 0);

  let footer: string;
  if (closed) {
    if (status.finalised) {
      // updatedAt is the bot's most recent write timestamp, which
      // tracks the finalisation flip closely enough for "closed X ago".
      const last = poll.answers.length === 0 ? null : pollMessage?.poll?.updatedAt ?? null;
      footer = last !== null ? `closed ${formatRelativePast(last)}` : 'closed';
    } else {
      footer =
        status.expiresAt !== null
          ? `closed ${formatRelativePast(status.expiresAt)}`
          : 'closed';
    }
  } else if (status.expiresAt !== null) {
    footer = `closes in ${formatTimeUntil(status.expiresAt)}`;
  } else {
    footer = 'no expiry';
  }

  return (
    <div
      ref={innerRef}
      className={cn(
        'transition-colors',
        highlighted && 'bg-info-bg ring-2 ring-info ring-inset',
      )}
    >
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="flex min-w-0 flex-1 items-center gap-1.5 text-sm font-medium text-text">
            <PollIcon className="h-4 w-4 shrink-0" />
            <span>{poll.question}</span>
          </h3>
          <span
            className={cn(
              'shrink-0 rounded border px-1.5 py-0.5 text-xs font-medium leading-none',
              closed
                ? 'border-border bg-surface-2 text-text-muted'
                : 'border-accent bg-accent-bg text-accent-text',
            )}
          >
            {closed ? 'Closed' : 'Active'}
          </span>
        </div>
        {poll.answers.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {poll.answers.map((a) => (
              <PollAnswerRow
                key={a.id}
                text={a.text}
                emoji={a.emoji}
                voteCount={a.voteCount}
                totalVotes={totalVotes}
                isLeader={a.voteCount === maxVotes && maxVotes > 0}
                closed={closed}
              />
            ))}
          </ul>
        )}
        <div className="mt-2 flex items-center justify-between text-xs text-text-muted">
          <span className="tabular-nums">
            {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
          </span>
          <span>{footer}</span>
        </div>
      </div>
      <FeedbackRow row={head} byExternalId={byExternalId} hidePollPill />
    </div>
  );
}
